import React from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { shallow } from 'zustand/shallow'
import { useOutletContext } from 'react-router-dom'
import { getStation, updateStation, type StationResponse } from '../../api/stations'
import { useSettingsIntegrations } from '../Settings'
import { usePageTitle } from '../../components/common/PageTitleContext'
import type { AppOutletContext } from '../../app/AppShell'
import useWeatherStore from '../../store/weather'
import {
  updateStationWeather,
  type WeatherManualRefreshResponse,
  type WeatherState,
} from '../../api/weather'
import { formatRelativeTime } from '../../utils/relativeTime'

const fallbackTimezones: readonly string[] = [
  'UTC',
  'Europe/London',
  'Europe/Paris',
  'Europe/Madrid',
  'Europe/Berlin',
  'Europe/Dublin',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Vancouver',
  'America/Sao_Paulo',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Asia/Dubai',
  'Australia/Sydney',
  'Pacific/Auckland',
]

const StationSettingsPage: React.FC = () => {
  usePageTitle('Station settings')
  const { notify } = useSettingsIntegrations()
  const ctx = useOutletContext<AppOutletContext | undefined>()

  if (!ctx) {
    return (
      <Stack spacing={3} component="section" alignItems="center">
        <CircularProgress size={24} />
      </Stack>
    )
  }

  const { me, activeStation, setActiveStation, updateMe } = ctx

  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [loadError, setLoadError] = React.useState<string | null>(null)
  const [validationError, setValidationError] = React.useState<string | null>(null)
  const [station, setStation] = React.useState<StationResponse | null>(null)
  const [timezone, setTimezone] = React.useState<string | null>(null)
  const [initialTimezone, setInitialTimezone] = React.useState<string | null>(null)
  const [weatherEnabled, setWeatherEnabled] = React.useState(false)
  const [weatherLocation, setWeatherLocation] = React.useState('')
  const [weatherUnits, setWeatherUnits] = React.useState<'metric' | 'imperial'>('metric')
  const [initialWeather, setInitialWeather] = React.useState({
    enabled: false,
    location: '',
    units: 'metric' as 'metric' | 'imperial',
  })
  const [weatherSaving, setWeatherSaving] = React.useState(false)
  const [weatherValidationError, setWeatherValidationError] = React.useState<string | null>(null)
  const [weatherSaveError, setWeatherSaveError] = React.useState<string | null>(null)
  const [autoRefreshQueued, setAutoRefreshQueued] = React.useState(false)

  const stationId = activeStation?.id ?? me.stations?.[0]?.id ?? null
  const stationName = station?.name ?? activeStation?.name ?? '—'

  const weatherSlice = useWeatherStore(
    React.useCallback(
      (state) => {
        if (!stationId) {
          return {
            data: null,
            loading: false,
            refreshing: false,
            hasFetched: false,
            error: null,
            manualRefresh: null,
          }
        }

        const entry = state.stations[stationId]

        if (!entry) {
          return {
            data: null,
            loading: false,
            refreshing: false,
            hasFetched: false,
            error: null,
            manualRefresh: null,
          }
        }

        return {
          data: entry.data ?? null,
          loading: entry.loading ?? false,
          refreshing: entry.refreshing ?? false,
          hasFetched: entry.hasFetched ?? false,
          error: entry.error ?? null,
          manualRefresh: entry.manualRefresh ?? null,
        }
      },
      [stationId],
    ),
    shallow,
  )

  const fetchWeather = useWeatherStore((state) => state.fetch)
  const manualRefreshWeather = useWeatherStore((state) => state.manualRefresh)
  const weatherData = weatherSlice.data
  const weatherLoading = weatherSlice.loading
  const weatherRefreshing = weatherSlice.refreshing
  const weatherErrorState = weatherSlice.error
  const weatherManualRefresh = weatherSlice.manualRefresh

  const resetState = React.useCallback(() => {
    setStation(null)
    setTimezone(null)
    setInitialTimezone(null)
    setValidationError(null)
  }, [])

  const applyWeatherStateToStore = React.useCallback((id: number, state: WeatherState) => {
    if (!Number.isFinite(id) || id <= 0) {
      return
    }

    useWeatherStore.setState((prev) => {
      const previous = prev.stations[id]
      const manualRefresh = previous?.manualRefresh ?? {
        status: null,
        message: null,
        errorCode: null,
        nextAllowedAt: null,
        occurredAt: null,
      }

      return {
        stations: {
          ...prev.stations,
          [id]: {
            stationId: id,
            data: state,
            loading: false,
            refreshing: false,
            hasFetched: true,
            error: null,
            manualRefresh,
            pollTimer: previous?.pollTimer ?? null,
            nextPollAt: previous?.nextPollAt ?? null,
          },
        },
      }
    })
  }, [])

  React.useEffect(() => {
    if (!stationId) {
      setWeatherEnabled(false)
      setWeatherLocation('')
      setWeatherUnits('metric')
      setInitialWeather({ enabled: false, location: '', units: 'metric' })
      setWeatherValidationError(null)
      setWeatherSaveError(null)
      setAutoRefreshQueued(false)
    }
  }, [stationId])

  React.useEffect(() => {
    let cancelled = false

    if (!stationId) {
      setLoading(false)
      setLoadError(null)
      resetState()
      return
    }

    setLoading(true)
    setLoadError(null)
    setValidationError(null)

    getStation(stationId)
      .then((response) => {
        if (cancelled) {
          return
        }

        setStation(response)
        setTimezone(response.timezone)
        setInitialTimezone(response.timezone)
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return
        }

        const message = resolveErrorMessage(error, 'Unable to load station details.')
        setLoadError(message)
        resetState()
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [stationId, resetState])

  React.useEffect(() => {
    if (!stationId) {
      return
    }

    const controller = new AbortController()

    fetchWeather(stationId, { signal: controller.signal }).catch(() => {
      // handled by the weather store
    })

    return () => {
      controller.abort()
    }
  }, [stationId, fetchWeather])

  React.useEffect(() => {
    if (!stationId || !weatherData) {
      return
    }

    const normalizedUnits = weatherData.units === 'imperial' ? 'imperial' : 'metric'
    const normalizedLocation = weatherData.location?.trim() ?? ''

    setWeatherEnabled(Boolean(weatherData.enabled))
    setWeatherLocation(normalizedLocation)
    setWeatherUnits(normalizedUnits)
    setInitialWeather({
      enabled: Boolean(weatherData.enabled),
      location: normalizedLocation,
      units: normalizedUnits,
    })
  }, [stationId, weatherData])

  React.useEffect(() => {
    if (!weatherRefreshing) {
      setAutoRefreshQueued(false)
    }
  }, [weatherRefreshing])

  const timezoneOptions = React.useMemo(() => {
    let options: string[] = []

    try {
      if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
        options = Intl.supportedValuesOf('timeZone')
      }
    } catch {
      options = []
    }

    if (!options.length) {
      options = [...fallbackTimezones]
    }

    const unique = new Set(options)

    if (timezone && timezone !== '') {
      unique.add(timezone)
    }

    return Array.from(unique).sort((a, b) => a.localeCompare(b))
  }, [timezone])

  const handleTimezoneChange = (_: unknown, value: string | null) => {
    setTimezone(value)
    setValidationError(null)
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!stationId) {
      return
    }

    if (!timezone) {
      setValidationError('Select a timezone from the list.')
      return
    }

    setSaving(true)
    setValidationError(null)

    try {
      const response = await updateStation(stationId, { timezone })
      setStation(response)
      setTimezone(response.timezone)
      setInitialTimezone(response.timezone)
      notify('success', 'Station timezone updated.')

      updateMe?.((prev) => ({
        ...prev,
        stations:
          prev.stations?.map((entry) =>
            entry.id === response.id ? { ...entry, timezone: response.timezone } : entry,
          ) ?? prev.stations,
      }))

      if (activeStation && activeStation.id === response.id) {
        setActiveStation({ id: response.id, name: response.name, timezone: response.timezone })
      }
    } catch (error) {
      const message = resolveErrorMessage(error, 'Unable to update station timezone.')
      const isInvalidTimezone = isRestError(error) && getErrorCode(error) === 'invalid_timezone'

      if (isInvalidTimezone) {
        setValidationError(message)
      } else {
        setValidationError(null)
      }

      notify('error', message)
    } finally {
      setSaving(false)
    }
  }

  const handleWeatherToggle = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setWeatherEnabled(checked)
    setWeatherValidationError(null)
  }

  const handleWeatherLocationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWeatherLocation(event.target.value)
    setWeatherValidationError(null)
  }

  const handleWeatherUnitsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = typeof event.target.value === 'string' ? event.target.value : 'metric'
    setWeatherUnits(value === 'imperial' ? 'imperial' : 'metric')
  }

  const handleWeatherSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!stationId) {
      return
    }

    const trimmedLocation = weatherLocation.trim()

    if (weatherEnabled && trimmedLocation.length < 3) {
      setWeatherValidationError('Enter a location with at least 3 characters.')
      return
    }

    setWeatherSaving(true)
    setWeatherValidationError(null)
    setWeatherSaveError(null)
    setAutoRefreshQueued(false)

    try {
      const response = await updateStationWeather(stationId, {
        weather_enabled: weatherEnabled,
        weather_location: trimmedLocation,
        weather_units: weatherUnits,
      })

      const normalizedUnits = response.units === 'imperial' ? 'imperial' : 'metric'
      const normalizedLocation = response.location?.trim() ?? ''

      setWeatherEnabled(Boolean(response.enabled))
      setWeatherLocation(normalizedLocation)
      setWeatherUnits(normalizedUnits)
      setInitialWeather({
        enabled: Boolean(response.enabled),
        location: normalizedLocation,
        units: normalizedUnits,
      })
      applyWeatherStateToStore(stationId, response)

      let notifySeverity: 'success' | 'error' = 'success'
      let notifyMessage = 'Weather settings updated.'

      if (response.enabled && !response.report) {
        setAutoRefreshQueued(true)
        const refreshResult: WeatherManualRefreshResponse | null = await manualRefreshWeather(stationId)

        if (!refreshResult) {
          notifySeverity = 'error'
          notifyMessage = 'Unable to schedule an immediate weather refresh.'
          setWeatherSaveError(notifyMessage)
        } else if (refreshResult.status === 'success') {
          notifySeverity = 'success'
          notifyMessage = refreshResult.message || 'Weather refresh requested.'
        } else if (refreshResult.status === 'cooldown') {
          notifySeverity = 'error'
          notifyMessage = refreshResult.message || 'Manual refresh is on cooldown.'
          setWeatherSaveError(notifyMessage)
        } else {
          notifySeverity = 'error'
          notifyMessage = refreshResult.message || 'Weather refresh request failed.'
          setWeatherSaveError(notifyMessage)
        }
      }

      notify(notifySeverity, notifyMessage)
    } catch (error) {
      const message = resolveErrorMessage(error, 'Unable to update weather settings.')
      setWeatherSaveError(message)
      notify('error', message)
    } finally {
      setWeatherSaving(false)
    }
  }

  const hasStation = Boolean(stationId)
  const trimmedWeatherLocation = weatherLocation.trim()
  const weatherFormDirty =
    weatherEnabled !== initialWeather.enabled ||
    trimmedWeatherLocation !== initialWeather.location ||
    weatherUnits !== initialWeather.units
  const weatherCooldownSeconds = React.useMemo(
    () => computeWeatherCooldownSeconds(weatherData?.cooldown ?? null),
    [weatherData?.cooldown?.expires_at, weatherData?.cooldown?.remaining, weatherData?.cooldown?.seconds],
  )
  const manualRefreshDisabledReason = React.useMemo(() => {
    if (!hasStation) {
      return 'Select a station to configure weather.'
    }

    if (weatherLoading) {
      return 'Weather status is still loading.'
    }

    if (weatherSaving) {
      return 'Save your changes before refreshing the weather.'
    }

    if (!weatherData) {
      return 'Weather data is not available yet.'
    }

    if (!weatherData.enabled) {
      return 'Enable weather updates to refresh manually.'
    }

    if (!weatherData.manual_refresh.presenter_enabled) {
      return 'Manual refresh is disabled for this station.'
    }

    if (!weatherData.manual_refresh.allowed_for_user) {
      return 'You do not have permission to refresh weather.'
    }

    if (weatherCooldownSeconds > 0) {
      return `Try again in ${formatCooldownLabel(weatherCooldownSeconds)}`
    }

    return null
  }, [hasStation, weatherCooldownSeconds, weatherData, weatherLoading, weatherSaving])
  const lastUpdatedEpoch = React.useMemo(
    () => resolveIsoEpoch(weatherData?.last_updated ?? weatherData?.report?.payload?.fetched_at ?? null),
    [weatherData?.last_updated, weatherData?.report?.payload?.fetched_at],
  )
  const lastUpdatedRelative = React.useMemo(() => formatRelativeTime(lastUpdatedEpoch), [lastUpdatedEpoch])
  const manualRefreshAlertRelative = React.useMemo(() => {
    if (!weatherManualRefresh?.occurredAt) {
      return null
    }
    return formatRelativeTime(weatherManualRefresh.occurredAt)
  }, [weatherManualRefresh?.occurredAt])
  const lastManualRequestRelative = React.useMemo(() => {
    const iso = weatherData?.manual_refresh?.last_manual_request_at ?? null
    const epoch = resolveIsoEpoch(iso)
    return epoch ? formatRelativeTime(epoch) : null
  }, [weatherData?.manual_refresh?.last_manual_request_at])
  const statusChips = React.useMemo(() => {
    const chips: React.ReactNode[] = []

    if (weatherRefreshing) {
      chips.push(
        <Chip
          key="weather-refreshing"
          icon={<CircularProgress size={16} />}
          label="Refreshing…"
          color="info"
          size="small"
        />,
      )
    } else {
      const statusMeta = resolveWeatherStatusChip(weatherData?.status ?? null)
      if (statusMeta) {
        chips.push(
          <Chip
            key="weather-status"
            label={statusMeta.label}
            color={statusMeta.color}
            size="small"
            variant={statusMeta.variant}
          />,
        )
      }
    }

    if (weatherData?.stale?.is_stale) {
      chips.push(
        <Chip
          key="weather-stale"
          label={resolveWeatherStaleLabel(weatherData.stale.reason)}
          color="warning"
          size="small"
          variant="outlined"
        />,
      )
    }

    if (weatherCooldownSeconds > 0) {
      chips.push(
        <Chip
          key="weather-cooldown"
          label={`Cooldown ${formatCooldownLabel(weatherCooldownSeconds)}`}
          color="warning"
          size="small"
          variant="outlined"
        />,
      )
    }

    if (autoRefreshQueued && !weatherRefreshing) {
      chips.push(
        <Chip
          key="weather-queued"
          label="Refresh requested"
          color="info"
          size="small"
          variant="outlined"
        />,
      )
    }

    return chips
  }, [autoRefreshQueued, weatherCooldownSeconds, weatherData?.stale?.is_stale, weatherData?.stale?.reason, weatherData?.status, weatherRefreshing])
  const manualRefreshSeverity = weatherManualRefresh?.status
    ? weatherManualRefresh.status === 'success'
      ? 'success'
      : weatherManualRefresh.status === 'cooldown'
        ? 'info'
        : 'error'
    : null
  const disableSave = saving || loading || !timezone || timezone === initialTimezone
  const disableWeatherSave =
    weatherSaving || weatherLoading || !hasStation || !weatherFormDirty || (weatherEnabled && trimmedWeatherLocation.length < 3)
  const handleManualRefresh = React.useCallback(() => {
    if (!stationId || weatherRefreshing || manualRefreshDisabledReason) {
      return
    }

    setAutoRefreshQueued(true)

    manualRefreshWeather(stationId).catch((error) => {
      if ((import.meta as any)?.env?.DEV) {
        console.error('[RPA] Manual weather refresh failed', error)
      }
    })
  }, [manualRefreshDisabledReason, manualRefreshWeather, stationId, weatherRefreshing])

  return (
    <Stack spacing={3} component="section">
      <Stack spacing={1}>
        <Typography variant="h4" component="h1">
          Station settings
        </Typography>
        <Typography color="text.secondary">
          Manage the timezone and weather configuration used across schedules, clocks, automations, and reporting.
        </Typography>
      </Stack>

      {!hasStation ? (
        <Alert severity="info">Select a station to configure timezone and weather.</Alert>
      ) : null}

      {loadError ? <Alert severity="error">{loadError}</Alert> : null}

      <Paper
        component="form"
        variant="outlined"
        onSubmit={handleSubmit}
        sx={{ borderRadius: 2, position: 'relative' }}
      >
        <Stack spacing={3} sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack spacing={1.5}>
            <TextField
              label="Station name"
              value={stationName}
              disabled
              InputProps={{ readOnly: true }}
            />
            <Autocomplete<string, false, false, false>
              disableClearable
              options={timezoneOptions}
              value={timezone}
              onChange={handleTimezoneChange}
              loading={loading}
              disabled={!hasStation || loading}
              isOptionEqualToValue={(option, value) => option === value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Timezone"
                  error={Boolean(validationError)}
                  helperText={
                    validationError ?? 'Choose the canonical IANA timezone for this station.'
                  }
                />
              )}
            />
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            <Button type="submit" variant="contained" disabled={disableSave || !hasStation}>
              Save changes
            </Button>
            {saving ? <CircularProgress size={20} /> : null}
          </Stack>
        </Stack>

        {loading ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : null}
      </Paper>

      <Paper
        component="form"
        variant="outlined"
        onSubmit={handleWeatherSubmit}
        sx={{ borderRadius: 2, position: 'relative' }}
      >
        <Stack spacing={3} sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack spacing={0.5}>
            <Typography variant="h5" component="h2">
              Presenter weather assistant
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure the automatic weather briefings shown in the app header and weather panel.
            </Typography>
          </Stack>

          {weatherErrorState ? (
            <Alert severity="error">{weatherErrorState.message}</Alert>
          ) : null}
          {weatherSaveError ? <Alert severity="error">{weatherSaveError}</Alert> : null}
          {manualRefreshSeverity && weatherManualRefresh?.message ? (
            <Alert severity={manualRefreshSeverity} role="status">
              {weatherManualRefresh.message}
              {manualRefreshAlertRelative ? (
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'block' }}
                  title={manualRefreshAlertRelative.tooltip ?? undefined}
                >
                  {manualRefreshAlertRelative.label}
                </Typography>
              ) : null}
            </Alert>
          ) : null}

          <Stack spacing={2.5}>
            <FormControlLabel
              control={
                <Switch
                  checked={weatherEnabled}
                  onChange={handleWeatherToggle}
                  disabled={!hasStation || weatherLoading || weatherSaving}
                  inputProps={{ 'aria-label': 'Enable presenter weather assistant' }}
                />
              }
              label="Enable presenter weather assistant"
            />

            <TextField
              label="Weather location"
              value={weatherLocation}
              onChange={handleWeatherLocationChange}
              disabled={!hasStation || weatherLoading || weatherSaving}
              required={weatherEnabled}
              error={Boolean(weatherEnabled && weatherValidationError)}
              helperText={
                weatherEnabled
                  ? weatherValidationError ?? 'Enter a city, postcode, or coordinates used for forecasts.'
                  : 'Set the default location used when weather is enabled.'
              }
            />

            <TextField
              label="Units"
              value={weatherUnits}
              onChange={handleWeatherUnitsChange}
              select
              disabled={!hasStation || weatherLoading || weatherSaving}
              helperText="Choose the measurement system for temperature and wind."
            >
              <MenuItem value="metric">Metric (°C, km/h)</MenuItem>
              <MenuItem value="imperial">Imperial (°F, mph)</MenuItem>
            </TextField>
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="subtitle2" color="text.secondary">
              Status
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {statusChips.length ? (
                statusChips
              ) : (
                <Chip key="weather-status-empty" label="No recent updates" size="small" variant="outlined" />
              )}
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              title={lastUpdatedRelative.tooltip ?? undefined}
            >
              Last successful update {lastUpdatedRelative.label}
            </Typography>
            {weatherCooldownSeconds > 0 ? (
              <Typography variant="body2" color="text.secondary">
                Manual refresh cooldown active · {formatCooldownLabel(weatherCooldownSeconds)} remaining
              </Typography>
            ) : null}
            {weatherData?.manual_refresh?.last_manual_request_at ? (
              <Typography
                variant="body2"
                color="text.secondary"
                title={lastManualRequestRelative?.tooltip ?? undefined}
              >
                Last manual request {lastManualRequestRelative?.label ?? '—'}
              </Typography>
            ) : null}
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Tooltip
              title={manualRefreshDisabledReason ?? ''}
              disableHoverListener={!manualRefreshDisabledReason}
              placement="top"
            >
              <span>
                <Button
                  variant="outlined"
                  onClick={handleManualRefresh}
                  disabled={Boolean(manualRefreshDisabledReason) || weatherRefreshing || weatherLoading || !hasStation}
                >
                  Refresh now
                </Button>
              </span>
            </Tooltip>
            {weatherRefreshing ? <CircularProgress size={20} /> : null}
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            <Button type="submit" variant="contained" disabled={disableWeatherSave}>
              Save weather settings
            </Button>
            {weatherSaving ? <CircularProgress size={20} /> : null}
          </Stack>
        </Stack>

        {weatherLoading ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : null}
      </Paper>
    </Stack>
  )
}

const formatCooldownLabel = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return ''
  }

  const totalSeconds = Math.max(0, Math.ceil(seconds))
  const minutes = Math.floor(totalSeconds / 60)
  const remainingSeconds = totalSeconds % 60

  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds.toString().padStart(2, '0')}s`
  }

  return `${remainingSeconds}s`
}

const computeWeatherCooldownSeconds = (cooldown: WeatherState['cooldown'] | null): number => {
  if (!cooldown) {
    return 0
  }

  if (Number.isFinite(cooldown.remaining ?? Number.NaN) && (cooldown.remaining as number) > 0) {
    return Math.ceil(Number(cooldown.remaining))
  }

  if (typeof cooldown.expires_at === 'string') {
    const epoch = resolveIsoEpoch(cooldown.expires_at)
    if (Number.isFinite(epoch ?? Number.NaN)) {
      const diff = (epoch as number) - Date.now()
      if (diff > 0) {
        return Math.ceil(diff / 1000)
      }
    }
  }

  if (Number.isFinite(cooldown.seconds ?? Number.NaN) && (cooldown.seconds as number) > 0) {
    return Math.ceil(Number(cooldown.seconds))
  }

  return 0
}

const resolveIsoEpoch = (value: string | null | undefined): number | null => {
  if (!value || typeof value !== 'string') {
    return null
  }

  const parsed = Date.parse(value)

  return Number.isNaN(parsed) ? null : parsed
}

type WeatherStatusChipMeta = {
  label: string
  color: 'default' | 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error'
  variant: 'filled' | 'outlined'
}

const resolveWeatherStatusChip = (status: string | null | undefined): WeatherStatusChipMeta | null => {
  if (!status || typeof status !== 'string') {
    return null
  }

  const normalized = status.trim().toLowerCase()

  if (!normalized) {
    return null
  }

  const map: Record<string, WeatherStatusChipMeta> = {
    success: { label: 'Last fetch succeeded', color: 'success', variant: 'filled' },
    error: { label: 'Last fetch failed', color: 'error', variant: 'filled' },
    cooldown: { label: 'Manual refresh cooling down', color: 'warning', variant: 'filled' },
    queued: { label: 'Refresh queued', color: 'info', variant: 'filled' },
    pending: { label: 'Refresh pending', color: 'info', variant: 'filled' },
  }

  if (map[normalized]) {
    return map[normalized]
  }

  const label = normalized.replace(/_/g, ' ')

  return { label: label.charAt(0).toUpperCase() + label.slice(1), color: 'info', variant: 'outlined' }
}

const resolveWeatherStaleLabel = (reason: WeatherState['stale']['reason']): string => {
  if (!reason) {
    return 'Fresh'
  }

  switch (reason) {
    case 'disabled':
      return 'Weather disabled'
    case 'missing':
      return 'No cached weather yet'
    case 'last_error':
      return 'Last fetch failed'
    case 'expired':
      return 'Forecast expired'
    default:
      return String(reason)
  }
}

function isRestError(error: unknown): error is Error & { status?: number; data?: unknown } {
  return typeof error === 'object' && error !== null && 'status' in error
}

function getErrorCode(error: unknown): string | null {
  if (!isRestError(error)) {
    return null
  }

  const data = (error as { data?: unknown }).data

  if (data && typeof data === 'object' && 'code' in data) {
    const code = (data as { code?: unknown }).code
    return typeof code === 'string' ? code : null
  }

  return null
}

function resolveErrorMessage(error: unknown, fallback: string): string {
  if (!error) {
    return fallback
  }

  if (isRestError(error)) {
    const data = (error as { data?: unknown }).data

    if (data && typeof data === 'object' && 'message' in data) {
      const message = (data as { message?: unknown }).message
      if (typeof message === 'string' && message.trim()) {
        return message
      }
    }

    if (typeof error.message === 'string' && error.message.trim()) {
      return error.message
    }
  } else if (error instanceof Error && typeof error.message === 'string') {
    return error.message
  }

  return fallback
}

export default StationSettingsPage
